import { motion } from "framer-motion";
import { Loader2, Clock, Sparkles } from "lucide-react";
import type { JobStatus } from "../api/client";

interface Props {
  status: JobStatus | "idle";
  elapsedMs: number;
  label?: string;
}

const STAGE_LABELS: Record<string, string> = {
  idle: "Ready",
  pending: "Queueing request...",
  running: "Claude Opus 4.7 is reasoning...",
  completed: "Complete",
  failed: "Failed",
};

export default function JobProgressIndicator({ status, elapsedMs, label }: Props) {
  if (status === "idle" || status === "completed") return null;

  const seconds = Math.floor(elapsedMs / 1000);
  const stageText = label || STAGE_LABELS[status] || "Working...";

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3"
      role="status"
      aria-live="polite"
    >
      {status === "failed" ? (
        <Clock className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
      ) : (
        <Loader2 className="text-nhs-blue animate-spin flex-shrink-0 mt-0.5" size={20} />
      )}
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-nhs-darkblue">{stageText}</p>
          <span className="text-xs text-gray-600 flex items-center gap-1">
            <Clock size={12} />
            {seconds}s
          </span>
          {status === "running" && (
            <span className="text-xs bg-white text-gray-600 px-2 py-0.5 rounded border border-gray-200 flex items-center gap-1">
              <Sparkles size={10} /> Opus 4.7
            </span>
          )}
        </div>
        <p className="text-xs text-gray-600 mt-1">
          {seconds < 5
            ? "Submitting your request..."
            : seconds < 15
              ? "Analysing sources and drafting response..."
              : seconds < 30
                ? "Generating detailed clinical content..."
                : "Still working — complex queries can take up to a minute..."}
        </p>
      </div>
    </motion.div>
  );
}
